'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { RadioGroup } from '@headlessui/react';
import EmojiText from '@/components/EmojiText/EmojiText';
import TextButton from '@/components/Button/TextButton';
import { mdiAutoFix, mdiDeleteSweep, mdiTrayArrowDown } from '@mdi/js';
import { JellyTriangle } from '@uiball/loaders';
import DrawingSubjects, { DrawingSubject } from './DrawingSubjects';
import { capitalizeFirstLetter } from '@/utils/strings';
import CharacterNotification from '@/components/CharacterNotification/CharacterNotification';
import { measure } from '@/components/Analytics/Analytics';

const DrawTogether = ({
    defaultDrawingSubject,
}: {
    defaultDrawingSubject: string;
}) => {
    const sketchRnnRef = useRef<HTMLIFrameElement>(null);

    const [sketchRnnFrameLoaded, setSketchRnnFrameLoaded] = useState(false);
    const [drawingSubject, setDrawingSubject] = useState<DrawingSubject>(
        DrawingSubjects[Object.keys(DrawingSubjects)[0]],
    );
    const [isLoadingAIModel, setIsLoadingAIModel] = useState(false);
    const [hasDrawnTogether, setHasDrawnTogether] = useState(false);

    useEffect(() => {
        // Update url 'tekening' param
        const url = new URL(window.location.href);
        url.searchParams.set('tekening', drawingSubject.urlId);
        window.history.replaceState({}, '', url.toString());
    }, [drawingSubject.urlId]);

    const onSketchRnnFrameLoaded = () => {
        setSketchRnnFrameLoaded(true);
        setDrawingSubject(
            DrawingSubjects[defaultDrawingSubject] ??
                DrawingSubjects[Object.keys(DrawingSubjects)[0]],
        );
    };
    const onSketchRnnFrameMessage = (event: {
        data: { _hetklokhuisaistudio: boolean; type: string };
    }) => {
        if (event.data?._hetklokhuisaistudio) {
            switch (event.data.type) {
                case 'ai.drawing':
                    if (!hasDrawnTogether) {
                        setHasDrawnTogether(true);
                    }
                    break;
                case 'class.loading':
                    7;
                    setIsLoadingAIModel(true);
                    break;
                case 'class.loaded':
                    setIsLoadingAIModel(false);
                    break;
            }
        }
    };

    useEffect(() => {
        if (sketchRnnRef.current) {
            sketchRnnRef.current.focus();
            sketchRnnRef.current.addEventListener(
                'load',
                onSketchRnnFrameLoaded,
            );
            window.addEventListener('message', onSketchRnnFrameMessage);
            sketchRnnRef.current.src = `/embedded/sketchrnn/index.html`;
        }

        return () => {
            window.removeEventListener('message', onSketchRnnFrameMessage);
            if (sketchRnnRef.current)
                sketchRnnRef.current.removeEventListener(
                    'load',
                    onSketchRnnFrameLoaded,
                );
        };
    }, [sketchRnnRef]);

    useEffect(() => {
        if (sketchRnnRef.current && sketchRnnFrameLoaded) {
            sketchRnnRef.current.contentWindow?.postMessage({
                _hetklokhuisaistudio: true,
                type: 'class.change',
                classId: drawingSubject.id,
            });
        }
    }, [drawingSubject.id]);

    const redrawPrediction = () => {
        measure('draw_together_redraw_prediction');
        if (sketchRnnRef.current && sketchRnnFrameLoaded) {
            sketchRnnRef.current.contentWindow?.postMessage({
                _hetklokhuisaistudio: true,
                type: 'drawing.redraw',
            });
        }
    };

    const saveDrawing = () => {
        measure('draw_together_save_drawing', 'download');
        if (sketchRnnRef.current && sketchRnnFrameLoaded) {
            sketchRnnRef.current.contentWindow?.postMessage({
                _hetklokhuisaistudio: true,
                type: 'drawing.download',
            });
        }
    };

    const deleteDrawing = () => {
        measure('draw_together_delete_drawing');
        if (sketchRnnRef.current && sketchRnnFrameLoaded) {
            sketchRnnRef.current.contentWindow?.postMessage({
                _hetklokhuisaistudio: true,
                type: 'drawing.clear',
            });
        }
    };

    return (
        <div className="flex flex-col lg:flex-row h-full w-full max-w-7xl mx-auto">
            <DrawingSubjectPicker
                drawingSubject={drawingSubject}
                setDrawingSubject={setDrawingSubject}
                isLoadingAIModel={!sketchRnnFrameLoaded || isLoadingAIModel}
            />
            <div className="grow flex flex-col-reverse md:flex-col gap-4 w-full h-full p-2 lg:p-4">
                {sketchRnnFrameLoaded && (
                    <div className="flex flex-wrap justify-center lg:justify-end gap-2 lg:gap-1 lg:pe-6 lg:-mb-9 z-10">
                        <TextButton
                            className="shrink-0 lg:rounded-r-md"
                            onClick={redrawPrediction}
                            icon={mdiAutoFix}
                            isEnabled={hasDrawnTogether}
                            isSmall
                        >
                            opnieuw afmaken
                        </TextButton>
                        <TextButton
                            className="shrink-0 lg:rounded-md"
                            onClick={saveDrawing}
                            icon={mdiTrayArrowDown}
                            isEnabled={hasDrawnTogether}
                            isSmall
                        >
                            opslaan
                        </TextButton>
                        <TextButton
                            className="shrink-0 lg:rounded-l-md from-orange-600 to-red-600"
                            onClick={deleteDrawing}
                            icon={mdiDeleteSweep}
                            isEnabled={hasDrawnTogether}
                            isSmall
                        >
                            wissen
                        </TextButton>
                    </div>
                )}
                <div className="relative grow w-full h-full">
                    <h1
                        className={`absolute top-0 lg:top-4 inset-x-0 w-full text-center pt-4 px-4 md:text-xl lg:text-2xl ${
                            sketchRnnFrameLoaded
                                ? 'text-slate-900'
                                : 'text-white'
                        }`}
                    >
                        Krabbel samen met AI {drawingSubject.displayName.long}
                    </h1>
                    {!sketchRnnFrameLoaded && (
                        <>
                            <p className="sr-only">AI laden</p>
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white">
                                <JellyTriangle color="currentColor" />
                            </div>
                        </>
                    )}
                    {isLoadingAIModel && (
                        <>
                            <p className="sr-only" role="status">
                                {drawingSubject.displayName.short} AI laden
                            </p>
                            <div
                                aria-hidden
                                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-4 bg-white/75 text-secondary-text-lg-aa rounded-full shadow-inner-md z-10"
                            >
                                <div className="animate-[spin_5s_linear_infinite]">
                                    <div className="-translate-y-1">
                                        <JellyTriangle
                                            color="currentColor"
                                            speed={0.85}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    <div className="absolute bottom-2 sm:bottom-4 md:top-4 left-2 sm:left-4">
                        <CharacterNotification
                            analyticsId="draw_together"
                            isAlreadyOpenedByDefault
                            showAfterSeconds={7}
                            characterId="tirsa_with"
                            actionButton={{
                                label: 'doen!',
                            }}
                            timelineBlocks={[
                                {
                                    type: 'chat_text',
                                    data: {
                                        character: 'tirsa_with',
                                        text:
                                            'Deze AI is getraind met duizenden krabbel-tekeningetjes! 😲🖍️',
                                    },
                                },
                                {
                                    type: 'chat_image',
                                    data: {
                                        alt_text:
                                            'Simpele tekeningen van een bloem, kat, brandweerauto, krab, een vogel en meer. Het zijn snelle tekeningen met simpele lijntjes (krabbels).',
                                    },
                                    media: {
                                        image: {
                                            url:
                                                '/embedded/sketchrnn/sketch_rnn_drawings_example.jpg',
                                        },
                                    },
                                },
                                {
                                    type: 'chat_text',
                                    data: {
                                        character: 'tirsa_with',
                                        text:
                                            "Nu kan het samen met jou zo'n tekeningetje maken 🤖🎨",
                                    },
                                },
                            ]}
                        />
                    </div>
                    <motion.iframe
                        ref={sketchRnnRef}
                        title="Teken AI"
                        className="w-full h-full bg-white rounded-xl md:rounded-2xl lg:rounded-3xl shadow-lg"
                        initial={{ opacity: 0 }}
                        animate={{
                            opacity: sketchRnnFrameLoaded ? 1 : 0,
                            pointerEvents: sketchRnnFrameLoaded
                                ? 'auto'
                                : 'none',
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

const DrawingSubjectPicker = ({
    drawingSubject,
    setDrawingSubject,
    isLoadingAIModel,
}: {
    drawingSubject: DrawingSubject;
    setDrawingSubject: (subject: DrawingSubject) => void;
    isLoadingAIModel: boolean;
}) => {
    const reduceMotion = useReducedMotion();

    const [hasRendered, setHasRendered] = useState(false);

    useEffect(() => {
        setHasRendered(true);
    }, []);

    useLayoutEffect(() => {
        // Scroll to selected subject
        setTimeout(() => {
            const selectedSubject = document.querySelector(
                '[data-selectedsubject="true"]',
            );
            if (selectedSubject) {
                selectedSubject.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'center',
                });
            }
        }, 500);
    }, [drawingSubject.id]);

    if (!hasRendered) return null;

    return (
        <RadioGroup
            value={drawingSubject}
            onChange={(subject) => {
                measure('draw_together_change_subject');
                measure(`draw_together_change_subject_${subject.id}`);
                setDrawingSubject(subject);
            }}
            className="shrink-0 flex flex-row lg:flex-col px-4 py-0 md:py-6 lg:p-8 lg:py-24 gap-4 overflow-x-auto overflow-y-hidden lg:overflow-y-auto"
            disabled={isLoadingAIModel}
        >
            <RadioGroup.Label className="sr-only">
                Kies een tekening
            </RadioGroup.Label>
            {Object.values(DrawingSubjects).map((subject) => (
                <RadioGroup.Option value={subject} key={subject.id}>
                    {({ checked, disabled }) => (
                        <div
                            className={`shrink-0 relative flex md:flex-col gap-2 md:gap-1 items-center justify-center min-w-[4rem] py-2 px-6 md:px-4 text-white lg:text-lg ${
                                disabled
                                    ? 'cursor-wait opacity-50'
                                    : 'cursor-pointer'
                            } transition-all`}
                            data-selectedsubject={checked}
                        >
                            {checked && (
                                <motion.div
                                    className="absolute inset-0 h-full w-full bg-gradient-to-b lg:bg-gradient-to-br from-secondary-text-base-aa/0 to-secondary-text-base-aaa border-b-[0.25rem] border-white rounded-lg shadow-lg -z-1"
                                    layoutId={
                                        reduceMotion
                                            ? undefined
                                            : 'drawtogetherwithai--selectedsubjectindicator'
                                    }
                                    layoutDependency={checked}
                                />
                            )}
                            <span
                                className={`shrink-0 text-2xl ${
                                    checked
                                        ? 'scale-150 md:scale-200 -rotate-6 md:-translate-y-2'
                                        : 'scale-125 hover:scale-150 hover:-rotate-3'
                                } transition-transform duration-700`}
                            >
                                <EmojiText>{subject.icon}</EmojiText>
                            </span>
                            <span
                                className={`shrink-0 ${
                                    checked
                                        ? ''
                                        : 'bg-secondary-text-base-aaa/75 shadow-md px-2'
                                } rounded-full transition-all duration-500`}
                            >
                                {capitalizeFirstLetter(
                                    subject.displayName.short,
                                )}
                            </span>
                        </div>
                    )}
                </RadioGroup.Option>
            ))}
        </RadioGroup>
    );
};

export default DrawTogether;
